import { useEffect, useRef } from 'react';
import { getMailWebSocketUrl } from '../lib/api';

/**
 * Subscribes to per-user mail WebSocket. Invokes `onEvent(data)` whenever
 * the server pushes a JSON message (e.g. { type: 'new_email', ... }).
 * Automatically reconnects with exponential backoff up to 30s.
 */
export function useMailWebSocket(userId, onEvent) {
    const handlerRef = useRef(onEvent);
    handlerRef.current = onEvent;

    useEffect(() => {
        if (!userId) return undefined;

        let ws = null;
        let closed = false;
        let retry = 0;
        const MAX_RETRIES = 5;  // stop retrying after ~30s total in envs without WS
        let reconnectTimer = null;
        let pingTimer = null;

        const connect = () => {
            if (closed) return;
            try {
                ws = new WebSocket(getMailWebSocketUrl(userId));
            } catch (e) {
                scheduleReconnect();
                return;
            }

            ws.onopen = () => {
                retry = 0;
                // keepalive
                pingTimer = setInterval(() => {
                    try { ws?.send(JSON.stringify({ type: 'ping' })); } catch (e) { /* noop */ }
                }, 30000);
            };

            ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(ev.data);
                    if (handlerRef.current) handlerRef.current(data);
                } catch (e) { /* noop */ }
            };

            ws.onerror = () => { /* handled via onclose */ };

            ws.onclose = () => {
                if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
                if (!closed) scheduleReconnect();
            };
        };

        const scheduleReconnect = () => {
            if (retry >= MAX_RETRIES) return;  // stop retrying (polling fallback covers this)
            const backoff = Math.min(30000, 1000 * Math.pow(2, retry));
            retry += 1;
            reconnectTimer = setTimeout(connect, backoff);
        };

        connect();

        return () => {
            closed = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (pingTimer) clearInterval(pingTimer);
            try { ws?.close(); } catch (e) { /* noop */ }
        };
    }, [userId]);
}
